import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { AsyncPaginate } from 'react-select-async-paginate';
import { useApolloClient } from '@apollo/client';
import { get as dig } from 'lodash';

import { tokens } from '@companycam/slab';
import CCEvents, {
  EventNames,
  EventPropertyKeys,
} from '@companycam/companycam-events';
import { REACT_SELECT_STYLES } from 'helpers/styles';
import { useCurrentCompany } from 'javascripts/hooks';
import CCModal from 'components/shared/DesignSystem/CCModal/CCModal';
import { Schema } from 'javascripts/graphql/Schema';

import GET_USERS_ASSIGNABLE_TO_PROJECT_QUERY from './graphql/getUsersAssignableToProject.graphql';
import GET_GROUPS from './graphql/getGroups.graphql';
import SelectOption from './SelectOption';
import useProjectUserCreate from './hooks/useProjectUserCreate';

import { useModal } from 'components/shared/hooks';
import { Modal } from 'components/core/modal/constants';
import { useModalToast } from 'components/shared/DesignSystem/ModalToast/useModalToast';

import { Steven } from 'helpers/steven';
import { getDisplayNameFromUser } from 'helpers/strings';
import { ButtonCondensed } from '@companycam/slab-web';
import { translate } from 'javascripts/translations/i18n';

// #region styled-components
const Container = styled.div`
  padding-block-end: var(--cc_size_spacing_l);
`;

const IntroPara = styled.p`
  margin: 0;
`;

const Label = styled.label`
  margin: ${tokens.size_spacing_l}px 0 ${tokens.size_spacing_s}px;
`;

// #endregion

const NoUsersFoundMessage = () => {
  return (
    <>
      {translate('projects.users.add.noUsersFound')}
      &nbsp;
      <ButtonCondensed color="primary" href="/invitations/new">
        {translate('projects.users.add.inviteNew')}
      </ButtonCondensed>
    </>
  );
};

const NoGroupsFoundMessage = () => {
  return (
    <>
      {translate('projects.users.add.noGroupsFound')}
      &nbsp;
      <ButtonCondensed color="primary" href="/invitations/new">
        {translate('projects.users.add.inviteNew')}
      </ButtonCondensed>
    </>
  );
};

const decomposeGroupIntoUsers = (group, excludeUsers) => {
  return group.users.nodes
    .filter((user) => !excludeUsers.some((xu) => xu.id === user.id))
    .map((user) => {
      const name = `${user.name} (${group.name})`;
      return { ...user, name };
    });
};

const getUserListForDisplay = (userList) => {
  const userListValues = userList.map((kv) => kv.value);
  return userListValues
    .map((userOrGroup) => {
      switch (userOrGroup.__typename) {
        case Schema.Types.Group:
          return decomposeGroupIntoUsers(userOrGroup, userListValues);
        case Schema.Types.User:
          return userOrGroup;
        default:
          return null;
      }
    })
    .flat()
    .filter((el) => !!el);
};

const getUserIdsForMutation = (userList) =>
  getUserListForDisplay(userList).map((user) => user.id);

const DialogState = {
  INITIAL: 'Initial',
  ERROR: 'Error',
};

const AddProjectUsersModal = ({ project, onSubmit, onRequestClose }) => {
  const client = useApolloClient();
  const { currentCompany } = useCurrentCompany();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [dialogState, setDialogState] = useState(DialogState.INITIAL);
  const [errorMessage, setErrorMessage] = useState(null);
  const { showToast } = useModalToast();

  const { showModal, hideModal } = useModal();

  const onModalClose = () => {
    onRequestClose();

    showModal(Modal.PROJECT_USERS, {
      onRequestClose: hideModal,
      projectId: project.id,
      projectTitle: project.title,
    });
  };

  const { createProjectUsers, loading: mutationInFlight } =
    useProjectUserCreate({
      projectId: project.id,
      onCompleted: (data) => {
        const addedUsersFromData = dig(
          data,
          'projectUserCreateInBulk.projectUsers',
          [],
        );
        CCEvents.logEvent(EventNames.ASSIGN_PROJECT, {
          [EventPropertyKeys.PROJECT_ID]: project.id,
          [EventPropertyKeys.PROJECT_NAME]: project.name,
          [EventPropertyKeys.ASSIGNED_USER_IDS]:
            getUserIdsForMutation(selectedUsers),
        });
        Steven.elementClicked({
          id: 'project_users_assigned',
          content: 'projects.users.add.submit',
          type: 'button',
          action: 'project_users_assigned',
        });

        const toastMessage = translate('projects.members.add.success', {
          count: addedUsersFromData.length,
        });

        showToast({
          type: 'success',
          message: toastMessage,
        });

        onSubmit();
        onModalClose();
      },
      onError: (error) => {
        setDialogState(DialogState.ERROR);
        setErrorMessage(error.message);
      },
    });

  const loadUsers = async (query, _, additional = {}) => {
    const currentlySelectedIds = selectedUsers.map((kv) => kv.value.id);
    const { data } = await client.query({
      query: GET_USERS_ASSIGNABLE_TO_PROJECT_QUERY,
      variables: {
        projectId: project.node_id,
        query,
        first: 50,
        after: additional.endCursor,
      },
      fetchPolicy: 'network-only',
    });
    const hasMore = dig(
      data,
      'viewer.usersAssignableToProject.pageInfo.hasNextPage',
      false,
    );
    const endCursor = dig(
      data,
      'viewer.usersAssignableToProject.pageInfo.endCursor',
      null,
    );
    return {
      options: dig(data, 'viewer.usersAssignableToProject.nodes', [])
        .map((node) => ({
          label: getDisplayNameFromUser(node),
          value: node,
        }))
        .filter(
          (option) =>
            !currentlySelectedIds.some((id) => id === option.value.id),
        ),
      hasMore,
      additional: { endCursor },
    };
  };

  const loadGroups = async (query, _, additional = {}) => {
    const currentlySelectedIds = selectedGroups.map((kv) => kv.value.id);
    const { data } = await client.query({
      query: GET_GROUPS,
      variables: {
        query,
        first: 50,
        after: additional.endCursor,
      },
      fetchPolicy: 'network-only',
    });
    const hasMore = dig(
      data,
      'viewer.company.groups.pageInfo.hasNextPage',
      false,
    );
    const endCursor = dig(
      data,
      'viewer.company.groups.pageInfo.endCursor',
      null,
    );
    return {
      options: dig(data, 'viewer.company.groups.nodes', [])
        .map((node) => ({
          label: node.name,
          value: node,
        }))
        .filter(
          (option) =>
            !currentlySelectedIds.some((id) => id === option.value.id),
        ),
      hasMore,
      additional: { endCursor },
    };
  };

  const renderModalTitle = () => {
    switch (dialogState) {
      case DialogState.CONFIRMING:
        return translate('projects.members.add.titleConfirm');
      default:
        return translate('projects.members.add.title');
    }
  };

  const handleSubmit = () => {
    createProjectUsers({
      variables: {
        input: {
          projectId: project.node_id,
          userIds: getUserIdsForMutation([...selectedUsers, ...selectedGroups]),
        },
      },
    });
  };

  return (
    <CCModal
      isOpen
      size="small"
      onRequestClose={onRequestClose}
      showCloseButton
      title={renderModalTitle()}
      label={translate('projects.members.add.ariaLabel')}
      primaryAction={{
        label: translate('projects.users.add.submit'),
        onClick: handleSubmit,
        disabled:
          (!selectedUsers.length && !selectedGroups.length) || mutationInFlight,
        type: 'submit',
        'data-testid': 'assign-users-modal__button-submit',
      }}
      secondaryAction={{
        label: translate('common.cancel'),
        disabled: mutationInFlight,
        onClick: onModalClose,
        'data-testid': 'assign-users-modal__button-cancel',
      }}
      alert={{
        isVisible: errorMessage && dialogState === DialogState.ERROR,
        color: 'destroy',
        iconName: 'alert-circle',
        iconAnimatesIn: true,
        title: translate('projects.members.add.titleError'),
        message: translate('projects.members.add.errorMessage', {
          errorMessage,
        }),
        actionLabel: translate('common.close'),
        actionOnClick: onModalClose,
      }}
      shouldAnimateIn={false}
    >
      {dialogState === DialogState.INITIAL && (
        <Container>
          <IntroPara>
            {translate('projects.members.add.searchModalSubtitle', {
              companyName: currentCompany.name,
            })}
          </IntroPara>

          <Label htmlFor="searchUsers">
            {translate('projects.members.add.searchUsersLabel')}
          </Label>

          <AsyncPaginate
            name="searchUsers"
            components={{
              Option: SelectOption,
            }}
            loadOptions={loadUsers}
            placeholder={translate('projects.members.add.placeholder.users')}
            debounceTimeout={250}
            value={selectedUsers}
            onChange={(value) => setSelectedUsers(value || [])}
            isMulti
            isClearable
            autoFocus
            loadOptionsOnMenuOpen
            loadingMessage={() =>
              translate('projects.members.add.loadingMessage', {
                companyName: currentCompany.name,
              })
            }
            noOptionsMessage={NoUsersFoundMessage}
            menuPortalTarget={document.body}
            styles={{
              ...REACT_SELECT_STYLES,
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />

          <Label htmlFor="searchGroups">
            {translate('projects.members.add.searchGroupsLabel')}
          </Label>

          <AsyncPaginate
            name="searchGroups"
            components={{
              Option: SelectOption,
            }}
            loadOptions={loadGroups}
            placeholder={translate('projects.members.add.placeholder.groups')}
            debounceTimeout={250}
            value={selectedGroups}
            onChange={(value) => setSelectedGroups(value || [])}
            isMulti
            isClearable
            loadOptionsOnMenuOpen
            loadingMessage={() =>
              translate('projects.members.add.loadingMessage', {
                companyName: currentCompany.name,
              })
            }
            noOptionsMessage={NoGroupsFoundMessage}
            menuPortalTarget={document.body}
            styles={{
              ...REACT_SELECT_STYLES,
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </Container>
      )}
    </CCModal>
  );
};

AddProjectUsersModal.propTypes = {
  project: PropTypes.object,
  onSubmit: PropTypes.func,
  onRequestClose: PropTypes.func,
};

export default AddProjectUsersModal;
